import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { SvgReportHeaderChips } from "../SvgReportHeaderChip/SvgReportHeaderChip.component";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { calSharingIndex } from "../functions/svg-dialog.functions";

export type SvgReportChipGroupProps = {
  /** Floor or section name; when omitted, the name chip is not shown (e.g. fleet report) */
  name?: string;
  averageUsers: string[];
  numWorkplaces: number;
  numParkings: number;
  numElectricchargings: number;
  numConferences: number;
  /** When set, show a single "Selected vehicles" chip and hide workplace/parking/electric/conference chips */
  isFleetReport: boolean;
  selectedVehicleCount?: number;
  avg: string;
  dev: string;
  currentFloorCapaUniqueIds: number[] | undefined;
  workplaceBookingEnabled: boolean;
};

export function SvgReportChipGroup({
  name,
  averageUsers,
  numWorkplaces,
  numParkings,
  numElectricchargings,
  numConferences,
  isFleetReport,
  workplaceBookingEnabled,
  selectedVehicleCount = 0,
  avg,
  dev,
  currentFloorCapaUniqueIds
}: Readonly<SvgReportChipGroupProps>) {
  const { t } = useTranslation();

  const usersTooltip = isFleetReport ? t("_fleetReport_numUsers") : t("Average users visited");
  const dayAverageTooltip = isFleetReport ? t("_fleetReport_capacity") : t("Day average");
  const dayStandardDeviationTooltip = isFleetReport
    ? t("_fleetReport_StandardDeviation")
    : t("Day standard deviation");

  return (
    <Box sx={{ mt: 1, ml: 1 }}>
      {/* floor name (optional; e.g. omitted for fleet report) */}
      {name != null && name !== "" && <SvgReportHeaderChips label={name} tooltip={name} />}

      {/* distinct Total Users */}
      <SvgReportHeaderChips
        label={averageUsers.length || 0}
        tooltip={t(usersTooltip)}
        icon={"users"}
      />

      {/* fleet: selected vehicles count */}
      {isFleetReport && (
        <SvgReportHeaderChips
          label={selectedVehicleCount}
          tooltip={t("_fleetReport_numVehicles")}
          icon={BookingType.VEHICLE}
        />
      )}

      {/* booking objects - workplaces (facility only) */}
      {!isFleetReport && !!numWorkplaces && workplaceBookingEnabled && (
        <SvgReportHeaderChips
          label={numWorkplaces}
          tooltip={t("Workplace")}
          icon={BookingType.WORKPLACE}
        />
      )}

      {/* booking objects - parking places */}
      {!isFleetReport && !!numParkings && (
        <SvgReportHeaderChips
          label={numParkings}
          tooltip={t("Parking")}
          icon={BookingType.PARKINGPLACE}
        />
      )}

      {/* booking objects - electric charging places */}
      {!isFleetReport && !!numElectricchargings && (
        <SvgReportHeaderChips
          label={numElectricchargings}
          tooltip={t("Electric Charging")}
          icon={BookingType.ELECTRICCHARGINGSTATIONPLACE}
        />
      )}

      {/* booking objects - conference zones */}
      {!isFleetReport && !!numConferences && (
        <SvgReportHeaderChips
          label={numConferences}
          tooltip={t("_bookTypeMeetingRoom")}
          icon={BookingType.CONFERENCEZONE}
        />
      )}

      {/* day average */}
      <SvgReportHeaderChips label={avg} tooltip={t(dayAverageTooltip)} />

      {/* day standard deviation */}
      <SvgReportHeaderChips label={dev} tooltip={t(dayStandardDeviationTooltip)} />

      {/* sharing index = #bookedUsers/#bookingObjects */}
      <SvgReportHeaderChips
        label={calSharingIndex(averageUsers, currentFloorCapaUniqueIds)}
        tooltip={t("Sharing Index")}
      />
    </Box>
  );
}
